import type { Product, SellingPlan } from '@/services/product'
import { defineStore } from 'pinia'

export interface NextOrderProductAddedAction {
  type: 'next_order_product_added'
  data: {
    productId: string
    variantId: string
    quantity: number
    sellingPlan: SellingPlan | null
    product: Product
  }
  autoDismiss: boolean
}

export interface NextOrderItemEditedAction {
  type: 'next_order_item_edited'
  data: {
    title: string
    frequency: number
    quantity: number
    scheduledAt: string
  }
  autoDismiss: boolean
}

export interface SubscriptionUpdatedAction {
  type: 'subscription_updated'
  data: {
    title: string
    frequency: number
    quantity: number
    scheduledAt: string
    imageSrc?: string
  }
  autoDismiss: boolean
}

export interface OnetimeUpdatedAction {
  type: 'onetime_updated'
  data: {
    title: string
    quantity: number
    scheduledAt: string
    imageSrc?: string
  }
  autoDismiss: boolean
}

export interface CartItemAddedAction {
  type: 'cart_item_added'
  data: {
    productId: string
    variantId: string
    quantity: number
    product: Product
  }
  autoDismiss: boolean
}

export interface PaymentMethodEmailSentAction {
  type: 'payment_method_email_sent'
  data: Record<string, never>
  autoDismiss: boolean
}

export type CompletedAction =
  | NextOrderProductAddedAction
  | NextOrderItemEditedAction
  | SubscriptionUpdatedAction
  | OnetimeUpdatedAction
  | CartItemAddedAction
  | PaymentMethodEmailSentAction

export const useCompletedActionStore = defineStore('completedAction', {
  state: () => ({
    lastCompletedAction: null as
      | (CompletedAction & { timestamp: number })
      | null,
    background: false,
    _backgroundTimeout: null as NodeJS.Timeout | null,
  }),

  actions: {
    setCompletedAction(action: CompletedAction, background: boolean = false) {
      if (this._backgroundTimeout) {
        clearTimeout(this._backgroundTimeout)
        this._backgroundTimeout = null
      }

      const actionWithTimestamp = {
        ...action,
        timestamp: Date.now(),
      } as CompletedAction & { timestamp: number }

      this.lastCompletedAction = actionWithTimestamp
      this.background = false
      if (background) {
        this.sendToBackground()
      }
    },

    sendToBackground() {
      this.background = true
      if (this.lastCompletedAction?.autoDismiss) {
        this._backgroundTimeout = setTimeout(() => {
          this.clearCompletedAction()
        }, 5000)
      }
    },

    clearCompletedAction() {
      if (this._backgroundTimeout) {
        clearTimeout(this._backgroundTimeout)
        this._backgroundTimeout = null
      }
      this.lastCompletedAction = null
      this.background = false
    },
  },
})
